import {
  ALTTVISION_BRIDGE_APP_ID,
  ALTTVISION_BRIDGE_CONTRACT_VERSION,
  createAlttvisionBridgeContract
} from '@shared/bridge-contract';
import { createAlttvisionApiClient } from '@shared/ipc/api-client';
import type { IpcChannel, IpcRequest, IpcResponse } from '@shared/ipc/contracts';
import { decodeIpcWireValue, encodeIpcWireValue } from '@shared/ipc/wire-serialization';

export const defaultBrowserDevBridgeUrl = 'http://127.0.0.1:53174';
const healthTimeoutMs = 2_500;

interface BrowserDevBridgeHealth {
  ok?: boolean;
  appId?: string;
  bridgeVersion?: string;
  databaseReady?: boolean;
}

interface BrowserDevBridgeInvokeResponse {
  ok?: boolean;
  result?: unknown;
  error?: {
    message?: string;
  };
}

function isElectronRenderer(): boolean {
  return typeof window !== 'undefined' && window.navigator.userAgent.toLowerCase().includes('electron');
}

function getBridgeUrl(): string {
  return window.alttvisionBrowserDevBridgeUrl ?? defaultBrowserDevBridgeUrl;
}

async function readJsonResponse(response: Response): Promise<BrowserDevBridgeInvokeResponse> {
  try {
    return (await response.json()) as BrowserDevBridgeInvokeResponse;
  } catch {
    return {
      ok: false,
      error: {
        message: `Bridge-ul browser a raspuns invalid (${response.status}).`
      }
    };
  }
}

async function assertHealthyBridge(bridgeUrl: string): Promise<void> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), healthTimeoutMs);

  let response: Response;
  try {
    response = await fetch(`${bridgeUrl}/health`, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('Bridge-ul browser nu a raspuns la timp.');
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(`Bridge-ul browser nu este disponibil (${response.status}).`);
  }

  const health = (await response.json()) as BrowserDevBridgeHealth;

  if (!health.ok || health.appId !== ALTTVISION_BRIDGE_APP_ID || health.bridgeVersion !== ALTTVISION_BRIDGE_CONTRACT_VERSION) {
    throw new Error('Bridge-ul browser nu expune contractul curent Alttvision Desktop.');
  }
}

async function invokeBrowserBridge<C extends IpcChannel>(
  channel: C,
  ...args: IpcRequest<C> extends void ? [] : [IpcRequest<C>]
): Promise<IpcResponse<C>> {
  const bridgeUrl = getBridgeUrl();
  const payload = args.length === 0 ? undefined : args[0];
  const response = await fetch(`${bridgeUrl}/api/invoke`, {
    method: 'POST',
    cache: 'no-store',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      channel,
      payload: encodeIpcWireValue(payload)
    })
  });
  const body = await readJsonResponse(response);

  if (!response.ok || !body.ok) {
    throw new Error(body.error?.message ?? `Bridge-ul browser a esuat pentru ${channel}.`);
  }

  return decodeIpcWireValue(body.result) as IpcResponse<C>;
}

export async function installBrowserDevBridgeIfNeeded(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  if (window.alttvision || window.alttvisionBridge || isElectronRenderer()) {
    return;
  }

  const bridgeUrl = getBridgeUrl();
  window.alttvisionBrowserDevBridgeUrl = bridgeUrl;

  try {
    await assertHealthyBridge(bridgeUrl);
    window.alttvision = createAlttvisionApiClient(invokeBrowserBridge);
    window.alttvisionBridge = createAlttvisionBridgeContract();
    window.alttvisionBrowserDevBridgeError = undefined;
  } catch (error) {
    window.alttvisionBrowserDevBridgeError =
      error instanceof Error ? error.message : 'Bridge-ul browser nu este disponibil.';
  }
}
